import { useMemo } from 'react';
import { cn } from '@/lib/utils';

/** FNV-1a: the same agent always gets the same avatar. */
function hash(seed: string) {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Small deterministic random generator (mulberry32) seeded from the agent. */
function random(seed: string) {
  let a = hash(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Body colours of the little workers: mostly sunny, now and then a rare one. */
const BODIES = [52, 48, 45, 40, 58, 30, 95, 160, 200, 280, 330];

/** How a Deskling feels right now: asleep (nothing to do), at work, waving for a person, down (it failed), cheering. */
export type DesklingMood = 'sleeping' | 'working' | 'waiting' | 'sad' | 'happy';

/**
 * A desk's Deskling as its pulse says (docs/features/desk-pulse.md#desklings): at work while it has a run; the Lead
 * waves while the desk waits for a person and works while it checks; asleep otherwise.
 */
export function desklingMood(pulse: { state: string; team: { agentId: string; role: string; working: boolean }[] } | null | undefined, agentId: string): DesklingMood | undefined {
  if (!pulse) return undefined;
  const member = pulse.team.find((m) => m.agentId === agentId);
  if (!member) return undefined;
  if (member.working) return 'working';
  if (member.role === 'lead') {
    if (pulse.state === 'needs_you') return 'waiting';
    if (pulse.state === 'checking') return 'working';
  }
  return 'sleeping';
}

/**
 * A Deskling, the little worker of a desk (docs/features/desk-pulse.md#desklings): a capsule with goggles, overalls and
 * a hair style or a hat — random per agent, stable across renders and sessions. Readable down to 18 px: the goggles carry
 * the face. `mood` shows what it is doing.
 */
export function AgentAvatar({
  seed,
  size = 48,
  className,
  muted,
  lead,
  mood,
}: {
  seed: string;
  size?: number;
  className?: string;
  muted?: boolean;
  /** The desk's Lead: a small crown in the corner. */
  lead?: boolean;
  mood?: DesklingMood;
}) {
  const a = useMemo(() => {
    const r = random(seed);
    const pick = <T,>(items: readonly T[]) => items[Math.floor(r() * items.length)];
    const hue = Math.floor(r() * 360);
    const body = pick(BODIES);
    // About every second one wears long hair, pigtails, a ponytail or a bun, lashes and lips.
    const feminine = r() > 0.5;
    return {
      feminine,
      hair: pick(['hsl(25 55% 28%)', 'hsl(42 85% 62%)', 'hsl(20 15% 15%)', 'hsl(12 75% 45%)', 'hsl(285 50% 45%)'] as const),
      id: `av-${hash(seed).toString(36)}`,
      bgFrom: `hsl(${hue} 70% 82%)`,
      bgTo: `hsl(${(hue + 40) % 360} 65% 66%)`,
      body: `hsl(${body} 92% 60%)`,
      bodyShade: `hsl(${body} 80% 48%)`,
      cloth: pick(['hsl(214 55% 46%)', 'hsl(222 45% 38%)', 'hsl(200 50% 42%)', 'hsl(160 35% 36%)', 'hsl(0 55% 48%)'] as const),
      iris: pick(['hsl(25 60% 32%)', 'hsl(200 60% 38%)', 'hsl(140 45% 32%)', 'hsl(30 25% 22%)'] as const),
      accent: `hsl(${(hue + 180) % 360} 80% 55%)`,
      eyes: pick(['two', 'two', 'one'] as const),
      look: (r() - 0.5) * 3,
      lid: pick(['none', 'none', 'none', 'sleepy', 'wink'] as const),
      mouth: feminine ? pick(['lips', 'lips', 'grin', 'teeth', 'o'] as const) : pick(['grin', 'teeth', 'o', 'tongue', 'smirk', 'gap'] as const),
      top: feminine
        ? pick(['long', 'long', 'pigtails', 'ponytail', 'bun', 'bow'] as const)
        : pick(['strands', 'sprout', 'tuft', 'bald', 'beanie', 'party', 'cap', 'headphones', 'bow'] as const),
      wave: r() > 0.6,
      cheeks: r() > 0.45,
      tall: r() > 0.5,
    };
  }, [seed]);

  // The mood takes over the face: closed eyes asleep, a tongue at work, a frown when down, teeth when cheering.
  const mouth =
    mood === 'sleeping' ? 'line' : mood === 'working' ? 'tongue' : mood === 'sad' ? 'frown' : mood === 'happy' ? 'teeth' : a.mouth;
  const lidStyle = mood === 'sleeping' ? 'closed' : mood ? 'none' : a.lid;
  const wave = mood === 'waiting' || mood === 'happy' || (a.wave && !mood);
  const top = a.tall ? 9 : 14;
  const eyeY = a.tall ? 27 : 30;
  const mouthY = eyeY + 13;
  const ink = 'hsl(220 30% 14%)';
  const rim = 'hsl(215 12% 66%)';
  const eyes: [number, number][] = a.eyes === 'one' ? [[32, 7.6]] : [[24.5, 5.6], [39.5, 5.6]];
  const hairCap = (
    <path
      d={`M14 ${top + 18} A18 18 0 0 1 50 ${top + 18} Q41 ${top + 9} 32 ${top + 11} Q23 ${top + 9} 14 ${top + 18} Z`}
      fill={a.hair}
      stroke={ink}
      strokeWidth="1"
    />
  );
  const lid = ([cx, r]: [number, number]) => (
    <path key={`lid-${cx}`} d={`M${cx - r} ${eyeY} a${r} ${r} 0 0 1 ${2 * r} 0 z`} fill={a.body} stroke={ink} strokeWidth="0.8" />
  );

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      aria-hidden
      className={cn('shrink-0 rounded-xl', muted && 'opacity-50 grayscale', className)}
    >
      <defs>
        <linearGradient id={a.id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={a.bgFrom} />
          <stop offset="1" stopColor={a.bgTo} />
        </linearGradient>
      </defs>
      <rect width="64" height="64" fill={`url(#${a.id})`} />
      <circle cx="54" cy="9" r="13" fill="white" opacity="0.22" />

      {/* A waving arm behind the body. */}
      {wave && (
        <g>
          <path d="M48 48 Q57 40 55 31" stroke={a.body} strokeWidth="5" fill="none" strokeLinecap="round" />
          <circle cx="55" cy="29.5" r="3.4" fill={ink} />
        </g>
      )}

      {/* Hair behind the body: long hair, pigtails, a ponytail. */}
      {a.top === 'long' && <rect x="10" y={top + 10} width="44" height="38" rx="9" fill={a.hair} stroke={ink} strokeWidth="1" />}
      {a.top === 'pigtails' && (
        <g fill={a.hair} stroke={ink} strokeWidth="1">
          <ellipse cx="10.5" cy={top + 17} rx="5" ry="8" />
          <ellipse cx="53.5" cy={top + 17} rx="5" ry="8" />
        </g>
      )}
      {a.top === 'ponytail' && <path d={`M46 ${top + 6} Q60 ${top + 10} 55 ${top + 32}`} stroke={a.hair} strokeWidth="7" fill="none" strokeLinecap="round" />}
      {a.top === 'bun' && <circle cx="32" cy={top - 2} r="6.5" fill={a.hair} stroke={ink} strokeWidth="1" />}

      {/* The capsule. */}
      <rect x="14" y={top} width="36" height={80 - top} rx="18" fill={a.body} stroke={ink} strokeWidth="1.2" />
      <path d={`M44 ${top + 8} q4 10 4 26`} stroke={a.bodyShade} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.5" />

      {/* Overalls: the bib with its buttons and pocket. */}
      <path d="M14 55 H50 V64 H14 Z" fill={a.cloth} />
      <path d="M22 51 H42 V58 H22 Z" fill={a.cloth} stroke={ink} strokeWidth="0.8" />
      <circle cx="24.5" cy="53.5" r="1.3" fill={ink} />
      <circle cx="39.5" cy="53.5" r="1.3" fill={ink} />
      <rect x="29" y="53" width="6" height="3.5" rx="1" fill="none" stroke={ink} strokeWidth="0.7" opacity="0.6" />

      {(a.top === 'long' || a.top === 'pigtails' || a.top === 'ponytail' || a.top === 'bun' || (a.feminine && a.top === 'bow')) && hairCap}
      {a.top === 'pigtails' && (
        <g fill={a.accent} stroke={ink} strokeWidth="0.8">
          <circle cx="14.5" cy={top + 12} r="2" />
          <circle cx="49.5" cy={top + 12} r="2" />
        </g>
      )}
      {a.top === 'ponytail' && <circle cx="47.5" cy={top + 7} r="2.2" fill={a.accent} stroke={ink} strokeWidth="0.8" />}

      {/* Goggles on their strap. */}
      <rect x="13.5" y={eyeY - 2.4} width="37" height="4.8" fill={ink} />
      {eyes.map(([cx, r]) => (
        <g key={cx}>
          <circle cx={cx} cy={eyeY} r={r + 2.6} fill={rim} stroke={ink} strokeWidth="1.2" />
          <circle cx={cx} cy={eyeY} r={r} fill="white" />
          <circle cx={cx + a.look} cy={eyeY + 0.4} r={r * 0.52} fill={a.iris} />
          <circle cx={cx + a.look} cy={eyeY + 0.4} r={r * 0.26} fill={ink} />
          <circle cx={cx + a.look + r * 0.22} cy={eyeY - r * 0.18} r={r * 0.13} fill="white" />
        </g>
      ))}
      {lidStyle === 'sleepy' && eyes.map(lid)}
      {lidStyle === 'wink' && lid(eyes[eyes.length - 1])}
      {lidStyle === 'closed' &&
        eyes.map(([cx, r]) => (
          <g key={`closed-${cx}`}>
            <circle cx={cx} cy={eyeY} r={r} fill={a.body} />
            <path d={`M${cx - r * 0.7} ${eyeY} q${r * 0.7} ${r * 0.6} ${r * 1.4} 0`} stroke={ink} strokeWidth="1.3" fill="none" strokeLinecap="round" />
          </g>
        ))}
      {(mood === 'working' || mood === 'sad') &&
        eyes.map(([cx, r]) => {
          const R = r + 2.6;
          // Brows: down to the middle when focused, up to the middle when sad.
          const inner = cx < 32 || eyes.length === 1 ? 1 : -1;
          const tilt = mood === 'working' ? 2.2 : -2.2;
          return (
            <path
              key={`brow-${cx}`}
              d={`M${cx - R * 0.8} ${eyeY - R - 2 - (inner > 0 ? 0 : tilt)} L${cx + R * 0.8} ${eyeY - R - 2 - (inner > 0 ? tilt : 0)}`}
              stroke={ink}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          );
        })}
      {a.feminine &&
        lidStyle !== 'closed' &&
        eyes.map(([cx, r]) => {
          const R = r + 2.6;
          return (
            <g key={`lashes-${cx}`} stroke={ink} strokeWidth="1.2" strokeLinecap="round">
              {[-125, -90, -55].map((deg) => {
                const rad = (deg * Math.PI) / 180;
                return (
                  <line
                    key={deg}
                    x1={cx + R * Math.cos(rad)}
                    y1={eyeY + R * Math.sin(rad)}
                    x2={cx + (R + 2.6) * Math.cos(rad)}
                    y2={eyeY + (R + 2.6) * Math.sin(rad)}
                  />
                );
              })}
            </g>
          );
        })}

      {a.cheeks && (
        <>
          <circle cx="19" cy={mouthY - 2} r="2.4" fill="hsl(350 85% 70%)" opacity="0.6" />
          <circle cx="45" cy={mouthY - 2} r="2.4" fill="hsl(350 85% 70%)" opacity="0.6" />
        </>
      )}

      {/* The mouth. */}
      {mouth === 'grin' && <path d={`M24 ${mouthY - 1} q8 7 16 0`} stroke={ink} strokeWidth="1.8" fill="none" strokeLinecap="round" />}
      {mouth === 'teeth' && (
        <g>
          <path d={`M24 ${mouthY - 2} q8 9 16 0 z`} fill={ink} />
          <path d={`M26 ${mouthY - 1.4} h12 v1.8 h-12 z`} fill="white" />
        </g>
      )}
      {mouth === 'o' && <ellipse cx="32" cy={mouthY} rx="2.6" ry="3.2" fill={ink} />}
      {mouth === 'line' && <path d={`M28 ${mouthY} h8`} stroke={ink} strokeWidth="1.8" strokeLinecap="round" />}
      {mouth === 'frown' && <path d={`M25 ${mouthY + 2} q7 -6 14 0`} stroke={ink} strokeWidth="1.8" fill="none" strokeLinecap="round" />}
      {mouth === 'lips' && <path d={`M25 ${mouthY - 1.5} q7 6.5 14 0 q-7 2 -14 0 z`} fill="hsl(345 80% 58%)" stroke={ink} strokeWidth="1" />}
      {mouth === 'tongue' && (
        <g>
          <path d={`M25 ${mouthY - 2} q7 7 14 0 z`} fill={ink} />
          <path d={`M30 ${mouthY + 0.5} q2 4 4 0 z`} fill="hsl(350 80% 62%)" />
        </g>
      )}
      {mouth === 'smirk' && <path d={`M26 ${mouthY} q7 3 13 -2`} stroke={ink} strokeWidth="1.8" fill="none" strokeLinecap="round" />}
      {mouth === 'gap' && (
        <g>
          <path d={`M24 ${mouthY - 2} q8 8 16 0 z`} fill={ink} />
          <rect x="28.6" y={mouthY - 1.8} width="2.6" height="2.4" fill="white" />
          <rect x="32.8" y={mouthY - 1.8} width="2.6" height="2.4" fill="white" />
        </g>
      )}

      {/* Hair or a hat. */}
      {a.top === 'strands' && (
        <g stroke={ink} strokeWidth="1.3" fill="none" strokeLinecap="round">
          <path d={`M29 ${top + 1} q-3 -5 -6 -6`} />
          <path d={`M32 ${top} q0 -6 1 -8`} />
          <path d={`M35 ${top + 1} q3 -5 6 -5`} />
        </g>
      )}
      {a.top === 'sprout' && (
        <g>
          <path d={`M32 ${top} q0 -5 1 -7`} stroke="hsl(130 45% 32%)" strokeWidth="1.6" fill="none" />
          <path d={`M33 ${top - 7} q5 -4 8 0 q-4 3 -8 0`} fill="hsl(125 55% 45%)" />
        </g>
      )}
      {a.top === 'tuft' && <path d={`M27 ${top + 2} q2 -8 5 -4 q2 -7 5 -1 q3 -5 4 3 z`} fill={ink} />}
      {a.top === 'beanie' && (
        <g>
          <path d={`M15 ${top + 9} q17 -18 34 0 z`} fill={a.accent} stroke={ink} strokeWidth="1" />
          <rect x="14" y={top + 6.5} width="36" height="4" rx="2" fill={a.accent} stroke={ink} strokeWidth="1" />
          <circle cx="32" cy={top - 1.5} r="2.6" fill="white" stroke={ink} strokeWidth="0.8" />
        </g>
      )}
      {a.top === 'party' && (
        <g>
          <path d={`M26 ${top + 2} L32 ${top - 9} L38 ${top + 2} z`} fill={a.accent} stroke={ink} strokeWidth="1" />
          <circle cx="32" cy={top - 9.5} r="2" fill="white" stroke={ink} strokeWidth="0.7" />
          <circle cx="30.5" cy={top - 2} r="0.9" fill="white" />
          <circle cx="33.5" cy={top - 4.5} r="0.9" fill="white" />
        </g>
      )}
      {a.top === 'cap' && (
        <g>
          <path d={`M17 ${top + 8} q15 -16 30 0 z`} fill={a.accent} stroke={ink} strokeWidth="1" />
          <path d={`M44 ${top + 8} h10 q1 2 -1 3 h-11 z`} fill={a.accent} stroke={ink} strokeWidth="1" />
        </g>
      )}
      {a.top === 'headphones' && (
        <g fill="none" stroke={ink} strokeWidth="2.2">
          <path d={`M14 ${eyeY - 4} q18 -${eyeY - top + 6} 36 0`} />
          <rect x="10" y={eyeY - 8} width="6" height="10" rx="2.5" fill={a.accent} strokeWidth="1" />
          <rect x="48" y={eyeY - 8} width="6" height="10" rx="2.5" fill={a.accent} strokeWidth="1" />
        </g>
      )}
      {a.top === 'bow' && (
        <g>
          <path d={`M32 ${top + 1} l-7 -5 v10 z M32 ${top + 1} l7 -5 v10 z`} fill={a.accent} stroke={ink} strokeWidth="0.9" />
          <circle cx="32" cy={top + 1} r="1.8" fill={a.accent} stroke={ink} strokeWidth="0.9" />
        </g>
      )}

      {mood === 'working' && <path d="M51 20 q-2.6 4 0 5.6 q2.6 -1.6 0 -5.6 z" fill="hsl(200 85% 70%)" stroke={ink} strokeWidth="0.7" />}
      {mood === 'sleeping' && (
        <g fill={ink} fontFamily="system-ui, sans-serif" fontWeight="700">
          <text x="47" y="18" fontSize="9">z</text>
          <text x="54" y="10" fontSize="7">z</text>
        </g>
      )}
      {mood === 'waiting' && (
        <g>
          <circle cx="51" cy="12" r="7" fill="white" stroke={ink} strokeWidth="1" />
          <path d="M51 8 v4.6" stroke="hsl(35 95% 50%)" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="51" cy="16" r="1.3" fill="hsl(35 95% 50%)" />
        </g>
      )}
      {mood === 'sad' && <path d={`M${eyes[0][0] - 2} ${eyeY + 9} q-1.8 3 0 4.4 q1.8 -1.4 0 -4.4 z`} fill="hsl(200 85% 70%)" stroke={ink} strokeWidth="0.6" />}
      {mood === 'happy' && (
        <g fill="hsl(45 95% 55%)" stroke={ink} strokeWidth="0.6">
          <path d="M8 22 l1.4 3 3 1.4 -3 1.4 -1.4 3 -1.4 -3 -3 -1.4 3 -1.4 z" />
          <path d="M50 8 l1 2.2 2.2 1 -2.2 1 -1 2.2 -1 -2.2 -2.2 -1 2.2 -1 z" />
        </g>
      )}

      {lead && (
        <g>
          <circle cx="11" cy="11" r="9" fill="white" opacity="0.9" />
          <path d="M4.5 14.5 L5.5 6.5 L8.6 10 L11 5 L13.4 10 L16.5 6.5 L17.5 14.5 Z" fill="hsl(45 95% 52%)" stroke={ink} strokeWidth="1" strokeLinejoin="round" />
        </g>
      )}
    </svg>
  );
}
